import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { ILegalEntity } from '@cemex-core/cmx-api';
import { CmxClientService, getAuth, getDirection, getLegalEntity, getMobile } from '@cemex/cmx-client-layout';
import { CwcSelectedOption, CwcSelectedOptions } from '@cmx-web-components/core/dist/types/components/select/types';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { QueryParams } from '@ordersnproducts/container/ordersnproducts.component';
import { PRODUCT_LINE_ALPHA_CODE } from '@ordersnproducts/modules/shared/constants/product-lines-codes';
import { distinctUntilChangedDeep } from '@ordersnproducts/modules/shared/utils/distinct-until-changed-deep';
import { _safe } from '@ordersnproducts/modules/shared/utils/safe-get.method';
import { combineLatest, Observable, Subject } from 'rxjs';
import { delay, distinctUntilChanged, filter, map, take, takeUntil } from 'rxjs/operators';
import { SelectJobsiteModalComponent } from '../components/select-jobsite-modal/select-jobsite-modal.component';
import { ALL_JOBSITES } from '../constants/all-jobsites.constant';
import { GlobalFiltersState, Jobsite, Pod, ProductLine } from '../models';
import { GlobalFiltersService } from '../services/global-filters.service';
import { SetJobsiteFilter, SetSelectingJobsite } from '../store/actions';
import {
  getAllJobsitesContent,
  getJobisteLoader,
  getJobsiteFilter,
  getJobsitesContent,
  getPodLoader,
  getPodsContentSelector,
  getProductLineLoader,
  getProductLinesContent,
  getSelectedJobsiteId,
  getSelectedProductLinesIds,
  getSelectingJobiste,
  selectFilterState,
} from '../store/selectors';
import { FeatureToggleService } from '@cemex-core/cmx-rollout-toggle-v1';
import { Features } from '@ordersnproducts/features';
import { OrdersSetCurrentPage } from '@ordersnproducts/modules/orders-and-product-catalog/modules/orders/actions/orders.actions';

@Component({
  selector: 'app-global-filters',
  templateUrl: './global-filters.component.html',
  styleUrls: ['./global-filters.component.scss'],
})
export class GlobalFiltersComponent implements OnInit, OnDestroy {
  @Input() calendarViewFilter = false;

  private ngDestroy = new Subject();
  private defaultJobsiteAlertId: number;

  entity: ILegalEntity;

  productLines$: Observable<ProductLine[]>;
  selectedProductLinesIds: number[] = [];
  productLineLoader$: Observable<boolean>;

  jobsites: Jobsite[] = [];
  allJobsites: Jobsite[] = [];
  selectedJobsite: Jobsite;
  preselectedJobsiteId: number;
  selectedJobsiteId$: Observable<number>;
  jobsiteLoader$: Observable<boolean>;
  jobsiteFilter: string;

  pods: Pod[] = [];
  podFilter: string;
  podLoader$: Observable<boolean>;
  disablePodFilter = false;

  // For now 1 represents the id for selecting all jobsites as a filter
  ALL_JOBSITES = ALL_JOBSITES;

  // If appendJobsite is set to true, currently selected jobsite will be displayed among the top jobsites in the doprdown
  appendJobsite = false;

  // ! If component no longer emit action on programatic changes, this should be removed
  // If selectingJobsite is set to true, events on product line dropdown are being ignored
  selectingJobsite = false;
  isProductLinePreselected: boolean;
  mobile$: Observable<boolean>;
  rtl$: Observable<boolean>;
  showCalendarViewAcces = false;
  openModal = false;
  calendarViewV2 = false;
  constructor(
    private filtersService: GlobalFiltersService,
    private store: Store<GlobalFiltersState>,
    private launcher: CmxClientService,
    private translate: TranslateService,
    private route: ActivatedRoute,
    private router: Router,
    public dialog: MatDialog,
    private featureToggle: FeatureToggleService,
  ) {}

  ngOnInit() {
    this.checkCalendarViewAccess();
    this.store
      .pipe(select(getLegalEntity), distinctUntilChangedDeep(), takeUntil(this.ngDestroy))
      .subscribe((entity) => {
        this.entity = entity;
        if (!!entity) {
          if (this.calendarViewFilter && !!entity) {
            // [6] is the id for readymix for productLines
            this.filtersService.setDefaultFiltersCalendarView(entity, ALL_JOBSITES, [6]);
          } else {
            this.filtersService.setFilters(entity);
          }
        }
      });

    this.setUpJobsites();
    if (this.calendarViewFilter) {
      this.setUpPod();
      this.podLoader$ = this.store.pipe(select(getPodLoader));
    }

    // Delay 1 is set to prevent Angular error
    this.store
      .pipe(select(getSelectedProductLinesIds), distinctUntilChanged(), delay(1))
      .subscribe((selectedProductLinesIds) => {
        this.selectedProductLinesIds = selectedProductLinesIds;
      });

    this.productLines$ = this.store.pipe(
      select(getProductLinesContent),
      // Remove aggregates
      map((lines) => {
        if (this.calendarViewFilter) {
          return lines.filter((productLine) => productLine.code !== PRODUCT_LINE_ALPHA_CODE.AGGREGATES);
        } else {
          return lines;
        }
      }),
      // Remove disabled Lines from options
      map((lines) => lines.filter((line) => !line.disabled)),
    );

    this.productLines$.pipe(distinctUntilChangedDeep(), take(1)).subscribe((productLines: ProductLine[]) => {
      this.isProductLinePreselected =
        productLines.filter((productLine) => !productLine.disabled).length === this.selectedProductLinesIds.length;
    });
    this.productLineLoader$ = this.store.pipe(select(getProductLineLoader));
    this.jobsiteLoader$ = this.store.pipe(select(getJobisteLoader));
    this.store.pipe(select(getSelectingJobiste), takeUntil(this.ngDestroy)).subscribe((selectingJobsite) => {
      this.selectingJobsite = selectingJobsite;
    });

    this.store
      .pipe(select(getJobsiteFilter), takeUntil(this.ngDestroy))
      .subscribe((value) => (this.jobsiteFilter = value));

    this.mobile$ = this.store.pipe(select(getMobile));
    this.rtl$ = this.store.pipe(
      select(getDirection),
      map((direction) => direction === 'rtl'),
    );

    // Detect one click
    const isOneClick = this.route.queryParams.pipe(map((routes) => QueryParams.OneClick in routes));

    // Show modal only when one click isn't selected
    isOneClick.pipe(take(1)).subscribe((is) => (!is ? this.initJobsiteModal() : null));

    this.calendarViewV2 = this.featureToggle.isFeatureEnabled(Features.CalendarViewNewVersion);
  }

  /**
   * Sets the top and all jobsites
   * Preselects jobsite when there is saved one in a localstorage
   */
  setUpJobsites(): void {
    const topFiveJobsites$ = this.store.pipe(select(getJobsitesContent));
    const allJobsites$ = this.store.pipe(select(getAllJobsitesContent));
    this.selectedJobsiteId$ = this.store.pipe(select(getSelectedJobsiteId));

    combineLatest([topFiveJobsites$, allJobsites$, this.selectedJobsiteId$])
      .pipe(distinctUntilChangedDeep(), takeUntil(this.ngDestroy), delay(1))
      .subscribe(([topJobsites, allJobsites, selectedJobsiteId]) => {
        this.jobsites = topJobsites;
        this.allJobsites = allJobsites;

        if (this.jobsites.some((jobsite) => jobsite.id === selectedJobsiteId) || selectedJobsiteId === ALL_JOBSITES) {
          this.preselectedJobsiteId = selectedJobsiteId;
        } else {
          if (this.jobsites.length) {
            this.preselectedJobsiteId = this.jobsites[0].id;
          }
        }

        // ! If component no longer emit action on programatic changes, this should be removed
        this.store.dispatch(new SetSelectingJobsite(false));

        // Preselect jobsite
        this.selectedJobsite = this.allJobsites.find((jobsite) => jobsite.id === selectedJobsiteId);
        this.appendJobsite =
          this.selectedJobsite && !this.jobsites.some((jobsite) => jobsite.id === this.selectedJobsite.id);

        if (this.calendarViewFilter && selectedJobsiteId === ALL_JOBSITES) {
          // This mock of POD is here because for some reason without any value, Pod Filter does not put placeholder
          this.pods = [{ id: 0, name: '', code: '' }];
          this.disablePodFilter = true;
        }
      });
  }

  setUpPod(): void {
    const getAllPodsContent = combineLatest([
      this.store.select(getLegalEntity),
      this.store.select(selectFilterState),
    ]).pipe(map(([entity, filterState]) => getPodsContentSelector(entity, filterState)));

    combineLatest([getAllPodsContent, this.store.pipe(select(getSelectedJobsiteId))])
      .pipe(takeUntil(this.ngDestroy))
      .subscribe(([allPods, selectedJobsiteId]) => {
        this.pods = allPods[selectedJobsiteId] ? allPods[selectedJobsiteId] : [];
      });
  }

  onProducLineSelect(event: CustomEvent<CwcSelectedOptions>) {
    if (!event.detail || !Array.isArray(event.detail) || (this.selectingJobsite && !this.isProductLinePreselected)) {
      return;
    }

    const selectedProductLinesIds = event.detail
      .map((item) => (item.value === null ? null : Number(item.value)))
      .filter((value) => value !== null);
    this.filtersService.selectProductLines(this.entity.legalEntityId, selectedProductLinesIds);
  }

  onJobsiteSelect(event: CustomEvent<CwcSelectedOption>) {
    this.store.dispatch(new OrdersSetCurrentPage(1));
    if (!event.detail) {
      return;
    }
    if (this.calendarViewFilter) {
      this.disablePodFilter = Number(event.detail.value) === this.ALL_JOBSITES;
    }
    const selectedJobsiteId = Number(event.detail.value);
    // ! If component no longer emit action on programatic changes, this should be removed
    this.store.dispatch(new SetSelectingJobsite(true));
    !!selectedJobsiteId &&
      this.filtersService.selectJobsite(this.entity, selectedJobsiteId, true, Boolean(this.calendarViewFilter));
    this.filtersService.selectPod(this.entity, null);
  }

  onJobsiteSearch(event: CustomEvent<string>) {
    if (!event) {
      return;
    }
    let searchString = event.detail;
    searchString = searchString && searchString.trim();
    if (this.jobsiteFilter === searchString) {
      return;
    }
    this.store.dispatch(new SetJobsiteFilter(searchString));
  }

  onPodSelect(event: CustomEvent<CwcSelectedOption>) {
    if (!event.detail) {
      return;
    }
    const selectedPodId = Number(event.detail.value);
    this.filtersService.selectPod(this.entity, selectedPodId);
  }

  onPodSearch(event: CustomEvent<string>) {
    if (!event) {
      return;
    }
    let searchString = event.detail;
    searchString = searchString && searchString.trim();
    if (this.podFilter === searchString) {
      return;
    }
  }

  navigateToCalendarView(): void {
    this.router.navigate(['/calendar-view']);
  }

  private initJobsiteModal() {
    this.filtersService.shouldOpenJobsiteModal
      .pipe(
        filter((value) => value),
        takeUntil(this.ngDestroy),
      )
      .subscribe(() => {
        if (!this.openModal) {
          this.selectFromModal();
        }
      });
  }

  private async selectFromModal() {
    const selectedJobsite = await this.openJobsiteModal();
    this.store.dispatch(new SetJobsiteFilter(''));
    if (selectedJobsite || !!this.preselectedJobsiteId) {
      const jobsite = selectedJobsite ? selectedJobsite.id : this.preselectedJobsiteId;
      await this.filtersService.selectJobsite(this.entity, jobsite, true, this.calendarViewFilter);
      return;
    }
    const defaultJobsite = this.allJobsites[0];
    if (defaultJobsite) {
      await this.filtersService.selectJobsite(this.entity, defaultJobsite.id, true, this.calendarViewFilter);
      if (this.defaultJobsiteAlertId) {
        this.launcher.closeMainAlert(this.defaultJobsiteAlertId);
      }
      this.defaultJobsiteAlertId = this.launcher.addMainAlert(
        'info',
        this.translate.instant('globalFilters.alert.defaultJobsite', { jobsite: defaultJobsite.name }),
      );
    }
  }

  private openJobsiteModal() {
    let dialogRef: MatDialogRef<SelectJobsiteModalComponent>;
    this.openModal = true;
    return new Promise<Jobsite>((res, rej) => {
      // Need to open in settimeout beaucse of angular bug.
      // Cannot open dialog synchronously in ngonInit call
      setTimeout(() => {
        dialogRef = this.dialog.open(SelectJobsiteModalComponent, {
          width: '676x',
          panelClass: 'mat-dialog-overflow-visible',
        });
        dialogRef
          .afterClosed()
          .pipe(take(1), takeUntil(this.ngDestroy))
          .subscribe((result) => {
            this.openModal = false;
            res(result);
          });
      });
    });
  }

  public checkCalendarViewAccess(): void {
    this.store.pipe(select(getAuth), takeUntil(this.ngDestroy)).subscribe((result) => {
      const calendarViewApp = _safe(() =>
        result.applications.find((application) => application.applicationCode === 'CalendarView_App'),
      );
      if (calendarViewApp) {
        return (this.showCalendarViewAcces = true);
      }
      return (this.showCalendarViewAcces = false);
    });
  }

  ngOnDestroy(): void {
    this.ngDestroy.next();
    this.ngDestroy.complete();
  }
}
